import { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "sonner";

const SpeechRecognitionAPI =
  typeof window !== "undefined"
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : null;

const MAX_DURATION_MS = 30_000; // 30 seconds

interface UseSpeechRecognitionOptions {
  lang?: string;
  onTranscript?: (text: string) => void;
}

export function useSpeechRecognition({
  lang = "de-DE",
  onTranscript,
}: UseSpeechRecognitionOptions = {}) {
  const [isListening, setIsListening] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(MAX_DURATION_MS / 1000);
  const recognitionRef = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  }, []);

  const stop = useCallback(() => {
    clearTimers();
    recognitionRef.current?.stop();
    setIsListening(false);
    setSecondsLeft(MAX_DURATION_MS / 1000);
  }, [clearTimers]);

  const start = useCallback(() => {
    if (!SpeechRecognitionAPI) {
      toast.error("Spracherkennung wird von deinem Browser nicht unterstützt.");
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
      setSecondsLeft(MAX_DURATION_MS / 1000);

      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, Math.ceil((MAX_DURATION_MS - elapsed) / 1000));
        setSecondsLeft(remaining);
      }, 500);

      // Auto-stop after 30s (but don't auto-send)
      timeoutRef.current = setTimeout(() => {
        recognition.stop();
      }, MAX_DURATION_MS);
    };

    recognition.onresult = (e: any) => {
      const transcript = Array.from(e.results)
        .map((r: any) => r[0].transcript)
        .join("");
      onTranscript?.(transcript);
    };

    recognition.onend = () => {
      clearTimers();
      setIsListening(false);
      setSecondsLeft(MAX_DURATION_MS / 1000);
      recognitionRef.current = null;
    };

    recognition.onerror = (e: any) => {
      console.error("Speech recognition error:", e.error);
      clearTimers();
      setIsListening(false);
      setSecondsLeft(MAX_DURATION_MS / 1000);
      recognitionRef.current = null;
      if (e.error === "not-allowed") {
        toast.error("Bitte erlaube den Mikrofonzugriff in deinen Browsereinstellungen.");
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [lang, onTranscript, clearTimers]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearTimers();
      recognitionRef.current?.stop();
    };
  }, [clearTimers]);

  return {
    isListening,
    secondsLeft,
    start,
    stop,
    isSupported: !!SpeechRecognitionAPI,
  };
}
